import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { formatDate } from '../utils/dateHelpers';
import pb from '../lib/pocketbaseClient';

export default function ProjectExpenses() {
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterProject, setFilterProject] = useState('All');
  const [filterCategory, setFilterCategory] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    project: '',
    category: 'material',
    description: '',
    amount: '',
    date: '',
    paidFrom: 'office_fund',
    receiptImage: null,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const isMounted = useRef(true);

  // Added 'food' to categories
  const categories = ['material', 'transport', 'equipment', 'labour', 'utility', 'food', 'other'];

  // Auto-select project from query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get('projectId');
    if (projectId) {
      setFormData(prev => ({ ...prev, project: projectId }));
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    fetchData();
    return () => { isMounted.current = false; };
  }, []);

  const fetchData = async () => {
    try {
      const [expensesRes, projectsRes, partnersRes] = await Promise.all([
        pb.collection('project_expenses').getFullList({ sort: '-date', expand: 'project,paidFrom' }),
        pb.collection('accounts_projects').getFullList({ sort: 'name' }),
        pb.collection('partners').getFullList({ sort: 'name' }),
      ]);
      if (isMounted.current) {
        setExpenses(expensesRes);
        setProjects(projectsRes);
        setPartners(partnersRes);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'receiptImage') {
      setFormData(prev => ({ ...prev, receiptImage: files[0] || null }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    // Validate required fields
    if (!formData.project || !formData.date || !formData.amount || !formData.description) {
      setFormError('Please fill in all required fields.');
      setFormLoading(false);
      return;
    }

    try {
      // Ensure date is in proper format (YYYY-MM-DD)
      const formattedDate = formData.date; // Already in YYYY-MM-DD format from input type="date"
      
      const data = {
        project: formData.project,
        category: formData.category,
        description: formData.description,
        amount: parseFloat(formData.amount) || 0,
        date: formattedDate,
        paidFrom: formData.paidFrom,
      };

      let saved;
      if (editingExpense) {
        saved = await pb.collection('project_expenses').update(editingExpense.id, data);
      } else {
        saved = await pb.collection('project_expenses').create(data);
      }

      if (formData.receiptImage) {
        const form = new FormData();
        form.append('receiptImage', formData.receiptImage);
        await pb.collection('project_expenses').update(saved.id, form);
      }

      handleCloseModal();
      fetchData();
    } catch (err) {
      console.error(err);
      let msg = err.message || 'Failed to save expense';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data).map(([k, v]) => `${k}: ${v.message}`).join('; ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    // Ensure date is properly formatted for the input field
    const expenseDate = expense.date || new Date().toISOString().split('T')[0];
    setFormData({
      project: expense.project || '',
      category: expense.category || 'material',
      description: expense.description || '',
      amount: expense.amount || '',
      date: expenseDate,
      paidFrom: expense.paidFrom || 'office_fund',
      receiptImage: null,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await pb.collection('project_expenses').delete(id);
      fetchData();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingExpense(null);
    // Reset form with today's date for new entries
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      project: '',
      category: 'material',
      description: '',
      amount: '',
      date: today, // Set to today's date
      paidFrom: 'office_fund',
      receiptImage: null,
    });
    setFormError('');
    setFormLoading(false);
  };

  const getPaidFromLabel = (id) => {
    if (id === 'office_fund') return '🏦 Office Fund';
    const partner = partners.find(p => p.id === id);
    return partner ? `🤝 ${partner.name}` : id;
  };

  const getCategoryBadge = (cat) => {
    const colors = {
      material: 'bg-blue-100 text-blue-700',
      transport: 'bg-purple-100 text-purple-700',
      equipment: 'bg-amber-100 text-amber-700',
      labour: 'bg-green-100 text-green-700',
      utility: 'bg-red-100 text-red-700',
      food: 'bg-orange-100 text-orange-700',
      other: 'bg-slate-100 text-slate-700',
    };
    return <Badge className={colors[cat] || colors.other}>{cat}</Badge>;
  };

  const filteredExpenses = expenses.filter(e => {
    const matchProject = filterProject === 'All' || e.project === filterProject;
    const matchCategory = filterCategory === 'All' || e.category === filterCategory;
    const matchSearch = e.description?.toLowerCase().includes(search.toLowerCase()) ||
                        e.expand?.project?.name?.toLowerCase().includes(search.toLowerCase());
    // Fix: Handle date comparison properly
    let matchDate = true;
    if (dateFrom && e.date) {
      matchDate = matchDate && e.date >= dateFrom;
    }
    if (dateTo && e.date) {
      matchDate = matchDate && e.date <= dateTo;
    }
    return matchProject && matchCategory && matchSearch && matchDate;
  });

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading expenses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">💰 Project Expenses</h1>
          <p className="text-sm text-slate-500">Track all project-related expenses</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => { setEditingExpense(null); setShowModal(true); }}>
          + New Expense
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Total Expenses</p><p className="text-2xl font-bold text-slate-800">₹{totalExpenses.toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Count</p><p className="text-2xl font-bold text-slate-800">{filteredExpenses.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Avg.</p><p className="text-2xl font-bold text-slate-800">{filteredExpenses.length > 0 ? `₹${(totalExpenses / filteredExpenses.length).toFixed(0)}` : '₹0'}</p></CardContent></Card>
      </div>

      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="flex-1 min-w-[200px]"><Input placeholder="🔍 Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full" /></div>
        <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400">
          <option value="All">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400">
          <option value="All">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
          <span className="text-slate-400">to</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
        </div>
        <Button variant="outline" onClick={() => { setSearch(''); setFilterProject('All'); setFilterCategory('All'); setDateFrom(''); setDateTo(''); }}>Clear</Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Date</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Project</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Description</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Category</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Paid From</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase text-right">Amount</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredExpenses.length === 0 ? (
                <tr><td colSpan="7" className="px-6 py-8 text-center text-slate-500">No expenses found.</td></tr>
              ) : (
                filteredExpenses.map(e => (
                  <tr key={e.id} className="hover:bg-slate-50/50 transition">
                    <td className="px-6 py-3">{formatDate(e.date)}</td>
                    <td className="px-6 py-3 font-medium">{e.expand?.project?.name || 'N/A'}</td>
                    <td className="px-6 py-3">{e.description}</td>
                    <td className="px-6 py-3">{getCategoryBadge(e.category)}</td>
                    <td className="px-6 py-3">{getPaidFromLabel(e.paidFrom)}</td>
                    <td className="px-6 py-3 text-right font-mono font-bold">₹{e.amount?.toLocaleString()}</td>
                    <td className="px-6 py-3 text-center space-x-2">
                      <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" onClick={() => handleEdit(e)}>Edit</Button>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(e.id)}>Delete</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-4">{editingExpense ? '✏️ Edit Expense' : '➕ New Expense'}</h2>
              {formError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200 whitespace-pre-wrap">{formError}</div>}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Project *</label>
                    <select name="project" value={formData.project} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                      <option value="">Select</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                    <select name="category" value={formData.category} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Description *</label><Input name="description" value={formData.description} onChange={handleChange} required /></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹) *</label><Input name="amount" type="number" value={formData.amount} onChange={handleChange} required step="0.01" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
                    <Input 
                      name="date" 
                      type="date" 
                      value={formData.date} 
                      onChange={handleChange} 
                      required 
                      max={new Date().toISOString().split('T')[0]} // Prevent future dates if needed
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Paid From *</label>
                  <select name="paidFrom" value={formData.paidFrom} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                    <option value="office_fund">🏦 Office Fund</option>
                    {partners.map(p => <option key={p.id} value={p.id}>🤝 {p.name}</option>)}
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Receipt</label><Input name="receiptImage" type="file" onChange={handleChange} accept="image/*" /></div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>{formLoading ? 'Saving...' : (editingExpense ? 'Update' : 'Create')}</Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}